// frontend/src/components/Dashboard/ArticleCard.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; // Import motion and AnimatePresence
import { saveArticle, unsaveArticleByArticleId } from '../../services/api';
import {
    BookmarkIcon as BookmarkOutlineIcon,
    ShareIcon as ShareOutlineIcon,
    EyeIcon as EyeOutlineIcon,
    TagIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    AcademicCapIcon, // For technical score
    PhotoIcon, // Placeholder for thumbnail
    ChevronDownIcon, // Added for expand/collapse
    SparklesIcon // For Gemini AI integration
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import ArticleDetailModal from './ArticleDetailModal';
import AIAnalysisModal from '../Common/AIAnalysisModal';
import { copyArticleForAnalysis } from '../../utils/geminiIntegration';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

const TopicTag = ({ topic, colorClass = "bg-blue-100 text-blue-700 hover:bg-blue-200" }) => (
  <span className={`inline-block text-xs font-semibold mr-1.5 mb-1 px-2.5 py-1 rounded-full transition-colors shadow-sm ${colorClass}`}>
    {topic}
  </span>
);

const NewArticleFlag = () => (
  <span className="ml-2 inline-block bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
    NEW
  </span>
);

const InfoTags = ({ items, icon: Icon, label, colorClass = "bg-gray-100 text-gray-700", itemHoverColorClass = "hover:bg-gray-200", maxToShow = 2 }) => {
    if (!items || items.length === 0) return null;
    const displayItems = items.slice(0, maxToShow);
    const remainingCount = items.length - maxToShow;

    return (
        <div className="mt-2.5 mb-1 flex items-center flex-wrap">
            {Icon && <Icon className={`h-4 w-4 mr-1.5 text-gray-500 flex-shrink-0`} />}
            {label && <span className="text-xs font-semibold text-gray-500 mr-1.5 whitespace-nowrap">{label}:</span>}
            {displayItems.map((item, index) => (
                <span key={index} className={`inline-block ${colorClass} text-xs font-medium mr-1.5 mb-1 px-2 py-0.5 rounded-full transition-colors ${itemHoverColorClass} shadow-sm`}>
                    {item}
                </span>
            ))}
            {remainingCount > 0 && (
                <span className="text-xs text-gray-500 ml-1">
                    +{remainingCount} more
                </span>
            )}
        </div>
    );
};


const ArticleCard = ({ article, onSaveToggle, expansionState = 2 }) => {
  const [isSaved, setIsSaved] = useState(article?.is_saved || false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [currentExpansionState, setCurrentExpansionState] = useState(expansionState); // 1, 2, or 3

  useEffect(() => {
    if (article) {
      setIsSaved(article.is_saved);
    }
  }, [article]);

  if (!article) return null;

  const {
    id: articleId,
    title, url, source_name, publication_date, classification,
    is_new,
    image_url, // Use actual image_url if available
    authors, // Assuming 'authors' is an array of strings
    estimated_read_time // Assuming this is a string like "5 min read"
  } = article;
  // const image_url = null; // Placeholder - remove if using actual image_url

  const summary = classification?.summary || "Summary not available.";
  const technicalScore = classification?.technical_score;
  const topics = classification?.topics || [];
  const contentTypes = classification?.content_types || [];
  const targetAudiences = classification?.target_audiences || [];
  const keyEntities = classification?.key_entities || {};

  const handleSaveToggle = async (e) => {
    e.stopPropagation();
    setIsSaving(true);
    setError('');
    try {
      if (isSaved) {
        await unsaveArticleByArticleId(articleId);
        setIsSaved(false);
        if (onSaveToggle) onSaveToggle(articleId, false);
      } else {
        await saveArticle(articleId);
        setIsSaved(true);
        if (onSaveToggle) onSaveToggle(articleId, true);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Action failed. Please try again.';
      setError(errorMessage);
      console.error("ArticleCard Save/Unsave error:", errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: `Check out this article: ${title}`,
          url: url,
        });
      } catch (shareError) {
        console.error('Error sharing article:', shareError);
        alert(`Could not share automatically. You can copy this link: ${url}`);
      }
    } else {
      navigator.clipboard.writeText(url).then(() => {
        alert('Link copied to clipboard!');
      }).catch(err => {
        alert(`Could not copy link automatically. Please copy this: ${url}`);
      });
    }
  };

  const handleGeminiAnalysis = (e) => {
    e.stopPropagation();
    setShowAIModal(true);
  };

  const handleOpenModal = (e) => {
    e.stopPropagation();
    setShowModal(true);
  };

  const toggleExpand = (e) => {
    e.stopPropagation();
    // Cycle through states: 1 -> 2 -> 3 -> 1
    setCurrentExpansionState(prev => prev === 3 ? 1 : prev + 1);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const SourceIcon = () => ( // Generic icon if no specific source icon is available
    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
    </svg>
  );

  const cardVariants = {
    hover: {
      scale: 1.02,
      boxShadow: "0px 12px 24px rgba(0,0,0,0.12)", // Enhanced shadow
      backgroundColor: "#f9fafb", // Slight background change on hover (Tailwind gray-50)
      transition: { duration: 0.2 }
    },
    tap: {
      scale: 0.98,
      transition: { duration: 0.1 }
    }
  };

  return (
    <>
      <motion.div 
        className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col border border-gray-200/80 group" 
        variants={cardVariants}
        whileHover="hover"
        // Removed whileTap from the main card to avoid conflict with button taps
      >
        {/* Image - shown in all states */}
        {image_url ? (
          <div className="w-full h-40 bg-gray-200 overflow-hidden">
            <img src={image_url} alt={title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
          </div>
        ) : (
          <div className="w-full h-40 bg-gray-100 flex items-center justify-center overflow-hidden border-b border-gray-200">
            <PhotoIcon className="w-16 h-16 text-gray-300 group-hover:text-gray-400 transition-colors" />
          </div>
        )}
        
        <div className="p-5 flex-grow flex flex-col"> 
          {/* Source info - shown in all states */}
          <div className="flex items-center text-xs text-gray-500 mb-2.5">
            <SourceIcon />
            <span className="ml-1.5 font-medium truncate" title={source_name}>{source_name || 'Unknown Source'}</span>
            {currentExpansionState >= 2 && authors && authors.length > 0 && (
              <span className="mx-1.5">&bull;</span>
            )}
            {currentExpansionState >= 2 && authors && authors.length > 0 && (
                <span className="truncate" title={authors.join(', ')}>{authors.slice(0,2).join(', ')}{authors.length > 2 ? ' et al.' : ''}</span>
            )}
            <span className="mx-1.5">&bull;</span>
            <span className="whitespace-nowrap">{formatDate(publication_date)}</span>
            {currentExpansionState >= 2 && estimated_read_time && (
                <>
                    <span className="mx-1.5">&bull;</span>
                    <span className="whitespace-nowrap">{estimated_read_time}</span>
                </>
            )}
            {is_new && <NewArticleFlag />}
          </div>

          {/* Title - shown in all states */}
          <h3 className="text-md sm:text-lg font-semibold text-gray-800 mb-2.5 leading-snug group-hover:text-blue-600 transition-colors">
            {title}
          </h3>

          {/* Summary - shown in all states, with different truncation */}
          <motion.div
            initial={false}
            animate={{
              height: currentExpansionState === 1 ? '40px' : 
                     currentExpansionState === 2 ? '40px' : 'auto',
              opacity: 1,
            }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden mb-1"
          >
            <p className={`text-sm text-gray-600 leading-relaxed ${
              currentExpansionState === 1 ? 'line-clamp-2' : 
              currentExpansionState === 2 ? 'line-clamp-2' : ''
            }`}>
              {summary}
            </p>
          </motion.div>

          <button
            onClick={toggleExpand}
            className="group inline-flex items-center text-xs text-blue-500 hover:text-blue-700 font-medium py-1 my-1 self-start transition-colors duration-150 ease-in-out focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-0 rounded"
            title={`Switch to ${currentExpansionState === 3 ? 'compact' : currentExpansionState === 1 ? 'with tags' : 'expanded'} view`}
          >
            {currentExpansionState === 1 ? 'Show with tags' : 
             currentExpansionState === 2 ? 'Show more' : 'Show less'}
            <motion.div
              animate={{ rotate: currentExpansionState === 3 ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="ml-1"
            >
              <ChevronDownIcon className="w-3.5 h-3.5" />
            </motion.div>
          </button>

          {error && <p className="text-xs text-red-500 my-1.5 p-1.5 bg-red-50 rounded">{error}</p>}

          {/* State 2: Show Tech Level and Topics */}
          {currentExpansionState === 2 && (
            <div className="mb-3 space-y-2"> 
              {technicalScore !== null && technicalScore !== undefined && (
                <div className="flex items-center">
                  <AcademicCapIcon className="h-4 w-4 mr-1.5 text-gray-500 flex-shrink-0" />
                  <span className="text-xs font-semibold text-gray-500 mr-1.5">Tech Level:</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full shadow-sm ${
                    technicalScore >= 4 ? 'bg-red-100 text-red-700' :
                    technicalScore >= 3 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {technicalScore}/5
                  </span>
                </div>
              )}

              {topics && topics.length > 0 && (
                <div className="flex items-start flex-wrap">
                   <TagIcon className="h-4 w-4 mr-1.5 text-gray-500 flex-shrink-0 mt-1" />
                   <span className="text-xs font-semibold text-gray-500 mr-1.5 whitespace-nowrap mt-1">Topics:</span>
                   <div className="flex flex-wrap items-center">
                      {topics.slice(0, 3).map((topic, index) => (
                        <TopicTag key={index} topic={topic} />
                      ))}
                      {topics.length > 3 && <span className="text-xs text-gray-500 ml-1 align-bottom mt-1"> +{topics.length - 3} more</span>}
                   </div>
                </div>
              )}
            </div>
          )}

          {/* State 3: Show all metadata and tags */}
          {currentExpansionState === 3 && (
            <div className="mb-3 space-y-2"> 
              {technicalScore !== null && technicalScore !== undefined && (
                <div className="flex items-center">
                  <AcademicCapIcon className="h-4 w-4 mr-1.5 text-gray-500 flex-shrink-0" />
                  <span className="text-xs font-semibold text-gray-500 mr-1.5">Tech Level:</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full shadow-sm ${
                    technicalScore >= 4 ? 'bg-red-100 text-red-700' :
                    technicalScore >= 3 ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {technicalScore}/5
                  </span>
                </div>
              )}

              {topics && topics.length > 0 && (
                <div className="flex items-start flex-wrap">
                   <TagIcon className="h-4 w-4 mr-1.5 text-gray-500 flex-shrink-0 mt-1" />
                   <span className="text-xs font-semibold text-gray-500 mr-1.5 whitespace-nowrap mt-1">Topics:</span>
                   <div className="flex flex-wrap items-center">
                      {topics.map((topic, index) => (
                        <TopicTag key={index} topic={topic} />
                      ))}
                   </div>
                </div>
              )}

              <InfoTags items={contentTypes} icon={TagIcon} label="Type" colorClass="bg-purple-100 text-purple-700 hover:bg-purple-200" maxToShow={contentTypes?.length || 0} />
              <InfoTags items={targetAudiences} icon={UserGroupIcon} label="Audience" colorClass="bg-teal-100 text-teal-700 hover:bg-teal-200" maxToShow={targetAudiences?.length || 0} />
              {keyEntities?.organizations && keyEntities.organizations.length > 0 && (
                   <InfoTags items={keyEntities.organizations} icon={BuildingOfficeIcon} label="Key Org" colorClass="bg-orange-100 text-orange-700 hover:bg-orange-200" maxToShow={keyEntities.organizations.length}/>
              )}
            </div>
          )}
          
          {/* View Details button - only shown in states 2 and 3 */}
          {currentExpansionState >= 2 && (
            <button
              onClick={handleOpenModal}
              className="group inline-flex items-center text-sm text-blue-600 hover:text-blue-800 font-semibold py-1.5 mt-auto transition-colors duration-150 ease-in-out focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 rounded"
              title="View more details"
            >
              <EyeOutlineIcon className="w-5 h-5 mr-1.5 group-hover:text-blue-700" />
              View Details
            </button>
          )}
        </div>

        {/* Action buttons - shown in all states */}
        <div className="px-4 py-2.5 border-t border-gray-200/70 flex justify-end items-center space-x-2 bg-gray-50/50">
          <button
            onClick={handleGeminiAnalysis}
            className="p-2 rounded-full hover:bg-gray-200/70 focus:outline-none focus:ring-1 focus:ring-purple-500 transition-colors"
            aria-label="Analyze with Gemini AI"
            title="Analyze with Gemini AI"
          >
            <SparklesIcon className="w-5 h-5 text-gray-500 hover:text-purple-600" />
          </button>
          <button
            onClick={handleSaveToggle}
            disabled={isSaving}
            className="p-2 rounded-full hover:bg-gray-200/70 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
            aria-label={isSaved ? "Unsave article" : "Save article"}
            title={isSaved ? "Unsave article" : "Save for later"}
          >
            {isSaving ? (
              <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : isSaved ? (
              <BookmarkSolidIcon className="w-5 h-5 text-blue-600" />
            ) : (
              <BookmarkOutlineIcon className="w-5 h-5 text-gray-500 hover:text-blue-600" />
            )}
          </button>
          <button
            onClick={handleShare}
            className="p-2 rounded-full hover:bg-gray-200/70 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
            aria-label="Share article"
            title="Share article"
          >
            <ShareOutlineIcon className="w-5 h-5 text-gray-500 hover:text-blue-600" />
          </button>
        </div>
      </motion.div>

      {showModal && (
        <ArticleDetailModal article={article} onClose={handleCloseModal} url={url} />
      )}
      
      {showAIModal && (
        <AIAnalysisModal 
          isOpen={showAIModal}
          onClose={() => setShowAIModal(false)}
          article={article}
          onAnalyze={copyArticleForAnalysis}
        />
      )}
    </>
  );
};
export default ArticleCard;
